import Logo from '@/components/logo';
import {Button} from '@/components/ui/button';
import {Input, Label} from '@/components/ui';
import {postEmployeeForgotPassword} from '@accumulus/shared';
import {ArrowLeft} from 'lucide-react';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'sonner';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const onSubmit = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    if (!isValidEmail(email)) {
      toast.error('The input is not a valid E-mail');
      return;
    }
    setIsLoading(true);
    try {
      const res = await postEmployeeForgotPassword(email);
      if (res.error) {
        toast.error(res.error_message || 'Failed to send reset email');
        return;
      }
      setSubmittedEmail(email);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen w-full items-center justify-center bg-primary-dark">
      <div className="w-[400px] rounded-lg border border-border bg-card p-6 shadow-md">
        <div className="flex justify-center pb-4">
          <Logo />
        </div>

        {submittedEmail ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-foreground">
              If an account exists for{' '}
              <span className="font-semibold">{submittedEmail}</span>, a new
              temporary password has been sent to that email address.
            </p>
            <p className="text-sm text-muted-foreground">
              Please check your inbox (and spam folder), then sign in with the
              temporary password and change it afterwards.
            </p>
            <Button
              className="w-full"
              onClick={() => navigate('/login')}
              disabled={isLoading}
            >
              <ArrowLeft size={16} />
              Back to Sign In
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter your email address and we&apos;ll send you a new temporary
              password.
            </p>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
                disabled={isLoading}
              />
            </div>
            <Button
              className="w-full"
              isLoading={isLoading}
              onClick={onSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Temporary Password'}
            </Button>
            <div className="text-center">
              <button
                type="button"
                className="text-sm text-mountain-meadow underline-offset-4 hover:underline"
                onClick={() => navigate('/login')}
                disabled={isLoading}
              >
                Back to Sign In
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
